import React, { useId, useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import { plural } from '@srm/ui/lib/pluralize';
import ResultMark, { ResultMarkState } from '@srm/ui/result-mark';
import { BackgroundJobKind } from '@models/backgroundJobs/IBackgroundJob';
import { BackgroundJobAction, backgroundJobKind } from '@services/backgroundJobs/backgroundJobKinds';
import { JOB_ACTION_ICONS } from '@services/backgroundJobs/jobActionIcons';
import { BackgroundJobRunPhase, IBackgroundJobWaitResult, resultWarningsCount } from './useBackgroundJobRun';

export type BackgroundJobWaitStagePhase = Exclude<BackgroundJobRunPhase, 'idle' | 'leaving'>;

export interface IBackgroundJobWaitStageProps {
    phase: BackgroundJobWaitStagePhase;
    kind?: BackgroundJobKind;
    /** Первая строка подписи лоадера: что ждём («Загружаем <b>План.xlsx</b>»). */
    subject: React.ReactNode;
    /** Текущий этап; без него — общая подпись вида. */
    stageText?: string;
    /** Работа идёт на сервере без окна — подсказка, что окно можно закрыть. */
    background?: boolean;
    result?: IBackgroundJobWaitResult;
    onDownload: () => void;
    /** Файл результата скачивается: кнопка показывает ход. */
    downloading?: boolean;
    /** Действия итога сверх файла; без обработчика они не показываются. */
    onAction?: (action: BackgroundJobAction) => void;
}

const MARK_STATE: Record<BackgroundJobWaitStagePhase, ResultMarkState> = {
    loading: 'loading',
    finishing: 'finishing',
    result: 'done',
};

const DownloadIcon = JOB_ACTION_ICONS.download;

/** Итог — одна-две фразы, а не построчный отчёт: остальное — в файле результата. */
const SHOWN_WARNINGS = 3;

const remarks = (count: number) => `${count} ${plural(count, ['замечание', 'замечания', 'замечаний'])}`;

const Warnings = ({ warnings, total }: { warnings: string[]; total: number }) => {
    const shown = warnings.slice(0, SHOWN_WARNINGS);
    const rest = total - shown.length;
    return (
        <Hint tone="warning" maxWidth="560px" className="bg-job-wait__warnings">
            {shown.map((warning, index) => (
                <span key={index} className="bg-job-wait__warning">
                    {warning}
                </span>
            ))}
            {rest > 0 && (
                <span className="bg-job-wait__warning">{shown.length ? `…и ещё ${remarks(rest)}` : remarks(rest)}</span>
            )}
        </Hint>
    );
};

/**
 * Ход работы и итог на одном месте: знак не двигается, заголовок итога встаёт туда, где была подпись лоадера.
 * Фокус идёт следом: кнопка, с которой ушли, на время работы заблокирована — без этого он упал бы в body.
 * Объявляет ход и итог не сам этап, а постоянная live-область окна (`backgroundJobWaitAnnouncement`).
 */
const BackgroundJobWaitStage = ({
    phase,
    kind,
    subject,
    stageText,
    background,
    result,
    onDownload,
    downloading,
    onAction,
}: IBackgroundJobWaitStageProps) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const fileNameId = useId();
    const hasResult = phase === 'result' && !!result;
    const texts = backgroundJobKind(kind).wait;
    const actions = onAction ? (result?.actions ?? []) : [];

    useLayoutEffect(() => {
        if (phase === 'loading') stageRef.current?.focus({ preventScroll: true });
    }, [phase]);

    useLayoutEffect(() => {
        if (hasResult) titleRef.current?.focus({ preventScroll: true });
    }, [hasResult]);

    return (
        <div ref={stageRef} tabIndex={-1} className={clsx('bg-job-wait__stage', `bg-job-wait__stage--${phase}`)}>
            <ResultMark state={MARK_STATE[phase]} tone={result?.tone ?? 'neutral'} />
            <p className="bg-job-wait__label" aria-hidden={phase !== 'loading'}>
                <span>{subject}</span>
                <span key={stageText} className="bg-job-wait__step">
                    {stageText ?? texts.runningLabel}
                </span>
                {background && <span className="bg-job-wait__hint">{texts.detachHint}</span>}
            </p>
            {hasResult && (
                <div className="bg-job-wait__result">
                    <h3 ref={titleRef} tabIndex={-1} className="bg-job-wait__title">
                        {result.title}
                    </h3>
                    <p className="bg-job-wait__text">{result.text}</p>
                    {result.warnings && <Warnings warnings={result.warnings} total={resultWarningsCount(result)} />}
                    {result.file && (
                        <div className="bg-job-wait__file">
                            <Button
                                variant="light"
                                size="sm"
                                startAdornment={<DownloadIcon size={16} aria-hidden />}
                                aria-describedby={fileNameId}
                                loading={downloading}
                                onClick={onDownload}
                            >
                                {result.tone === 'err' ? texts.downloadLabels.err : texts.downloadLabels.ok}
                            </Button>
                            <span id={fileNameId} className="bg-job-wait__file-name" title={result.file.name}>
                                {result.file.name}
                            </span>
                        </div>
                    )}
                    {actions.length > 0 && (
                        <div className="bg-job-wait__actions">
                            {actions.map((action, index) => (
                                <Button
                                    key={`${action.type}-${index}`}
                                    variant="light"
                                    size="sm"
                                    onClick={() => onAction?.(action)}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BackgroundJobWaitStage;
